<script setup lang="ts">
import { computed, onMounted, ref, watch, watchEffect } from 'vue';
import { SearchFilter, FilterOption, StepperValueLabelMapping, RangeBounds } from '../../../search-panel-types';
import FilterFieldLayout from './FilterFieldLayout.vue';
import FilterFormattedStepper from './FilterFormattedStepper.vue';
import useLocale from '../../../composables/useLocale';
import type { ComponentPublicInstance } from 'vue';

const emit = defineEmits<{
  (e: 'range-updated', range: RangeBounds): void;
}>();

type Props = {
  items: FilterOption[];
  fieldKey: string;
  label: string;
  selected: SearchFilter;
  customRange: number[] | null;
  customLabel?: boolean;
  multiple?: boolean;
  valueLabelMappings?: StepperValueLabelMapping[];
};
const props = defineProps<Props>();

const { t } = useLocale();

const minAriaLabel = computed(() => `${t('content_search.label_number_input_min')} ${t(props.label)}`);
const maxAriaLabel = computed(() => `${t('content_search.label_number_input_max')} ${t(props.label)}`);

// The number input component cannot format units automatically. So, if we want to display
// duration in minutes, both the model value AND the max/min limits must be converted to minutes.
// Otherwise, the value will 'clamp', which triggers an unwanted filter update.
const isDuration = computed(() => props.fieldKey === 'duration');
const numberInputMin = computed(() => (isDuration.value ? Math.floor(minRangeValue.value / 60) : minRangeValue.value));
const numberInputMax = computed(() => (isDuration.value ? Math.ceil(maxRangeValue.value / 60) : maxRangeValue.value));
const numberInputMinValue = computed(() => (isDuration.value ? Math.floor(range.value[0] / 60) : range.value[0]));
const numberInputMaxValue = computed(() => (isDuration.value ? Math.ceil(range.value[1] / 60) : range.value[1]));

// Do not allow negative values
const findMinRangeValue = () => {
  const min = props.items.reduce(
    (prev: number, cur: FilterOption) => (Number(prev) < Number(cur.value) ? prev : Number(cur.value)),
    Infinity,
  );

  if (min === Infinity) {
    return 0;
  }

  return Math.max(min, 0);
};

const findMaxRangeValue = () =>
  props.items.reduce(
    (prev: number | null, cur: FilterOption) => (Number(prev) > Number(cur.value) ? prev : Number(cur.value)),
    null,
  ) ?? 0;

const minRangeValue = ref(findMinRangeValue());
const maxRangeValue = ref(findMaxRangeValue());
const defaultRange = [minRangeValue.value, maxRangeValue.value];
const range = ref<number[]>([findMinRangeValue(), findMaxRangeValue()]);
const foundedMinRangeValue = ref<number>(minRangeValue.value);
const foundedMaxRangeValue = ref<number>(maxRangeValue.value);

const minInputRef = ref<ComponentPublicInstance | null>(null);
const maxInputRef = ref<ComponentPublicInstance | null>(null);

range.value = defaultRange;
watch(
  () => props.customRange,
  () => {
    if (props.customRange !== null) {
      range.value = props.customRange;
      return;
    }
    range.value = defaultRange;
  },
);

watchEffect(() => {
  if (props.customRange && props.customRange.length === 0) {
    foundedMinRangeValue.value = findMinRangeValue();
    foundedMaxRangeValue.value = findMaxRangeValue();
    if (Number(foundedMaxRangeValue.value) > maxRangeValue.value) {
      maxRangeValue.value = Number(props.customRange[1]);
    }
    if (Number(foundedMinRangeValue.value) < minRangeValue.value) {
      minRangeValue.value = Number(props.customRange[0]);
    }
    if (!props.selected[props.fieldKey]) {
      range.value = [minRangeValue.value, maxRangeValue.value];
    }
  } else {
    foundedMinRangeValue.value = props.customRange !== null ? props.customRange[0] : minRangeValue.value;
    foundedMaxRangeValue.value = props.customRange !== null ? props.customRange[1] : maxRangeValue.value;
    // if we get new values from api we need to setup right range but only for cases when the range is less
    // than minimum or more than maximum
    if (Number(foundedMaxRangeValue.value) > maxRangeValue.value && props.customRange) {
      maxRangeValue.value = Number(props.customRange[1]);
    }
    if (Number(foundedMinRangeValue.value) < minRangeValue.value && props.customRange) {
      minRangeValue.value = Number(props.customRange[0]);
    }
    if (!props.selected[props.fieldKey]) {
      if (props.customRange) {
        range.value = props.customRange;
      } else {
        range.value = [minRangeValue.value, maxRangeValue.value];
      }
    }
  }
});

const handleUpdate = () => {
  emit('range-updated', {
    min: range.value[0],
    max: range.value[1],
  });
};

const rangeContainerRef = ref<HTMLElement | null>(null);

/**
 * Disable tab focus for the range slider. Hide vertical and horizontal splitters
 * in the number input field from screen readers.
 */
onMounted(() => {
  const slider = rangeContainerRef.value?.querySelector('.v-slider__container');
  if (slider) {
    slider.querySelectorAll('.v-slider-thumb ').forEach((thumb) => {
      thumb.setAttribute('tabindex', '-1');
      thumb.setAttribute('aria-hidden', 'true');
    });
  }

  const splitters = rangeContainerRef.value?.querySelectorAll('hr');
  if (splitters) {
    splitters.forEach((splitter) => {
      splitter.setAttribute('aria-hidden', 'true');
    });
  }
});

const onMinChanged = (val: number) => {
  const value = Math.min(isDuration.value ? val * 60 : val, range.value[1]);
  if (Number.isFinite(value)) {
    range.value[0] = value;
  }

  const isFocused = minInputRef.value?.$el.contains(document.activeElement);
  if (!isFocused) {
    // If the change was triggered through increment/decrement buttons, update immediately
    handleUpdate();
  }
};

const onMaxChanged = (val: number) => {
  const value = Math.max(isDuration.value ? val * 60 : val, range.value[0]);
  if (Number.isFinite(value)) {
    range.value[1] = Math.min(value, Math.min(Number.MAX_SAFE_INTEGER, maxRangeValue.value));
  }
  const isFocused = maxInputRef.value?.$el.contains(document.activeElement);
  if (!isFocused) {
    // If the change was triggered through increment/decrement buttons, update immediately
    handleUpdate();
  }
};
</script>

<template>
  <template v-if="!props.multiple">
    <FilterFieldLayout v-show="props.items?.length > 0" :label="props.label ?? ''">
      <template #content>
        <div class="range-container">
          <v-range-slider
            v-model="range"
            color="primary"
            hide-details
            thumb-label="always"
            :max="maxRangeValue"
            :min="minRangeValue"
            step="1"
            track-size="1"
            thumb-size="12"
            @end="handleUpdate"
          >
            <template #thumb-label="rangeProps">
              <slot v-if="props.customLabel" :value="rangeProps.modelValue"></slot>
              <span v-else>
                {{ rangeProps.modelValue }}
              </span>
            </template>
          </v-range-slider>
        </div>
      </template>
    </FilterFieldLayout>
  </template>
  <template v-else>
    <div class="range-container d-flex flex-column ga-2" ref="rangeContainerRef">
      <v-range-slider
        v-model="range"
        color="primary"
        hide-details
        thumb-label="always"
        :max="maxRangeValue"
        :min="minRangeValue"
        step="1"
        track-size="1"
        thumb-size="12"
        @end="handleUpdate"
        :aria-hidden="true"
      >
        <template #thumb-label="rangeProps">
          <slot v-if="props.customLabel" :value="rangeProps.modelValue"></slot>
          <span v-else>
            {{ rangeProps.modelValue }}
          </span>
        </template>
      </v-range-slider>
      <div class="d-flex justify-space-between ga-2">
        <FilterFormattedStepper
          v-if="props.valueLabelMappings"
          :model-value="range[0]"
          :min="minRangeValue"
          :max="maxRangeValue"
          :value-label-mappings="props.valueLabelMappings"
          :ariaLabel="minAriaLabel"
          class="formatted-inputs"
          @update:modelValue="onMinChanged"
          @change="handleUpdate"
        />
        <v-number-input
          v-else
          ref="minInputRef"
          :model-value="numberInputMinValue"
          density="compact"
          :max="numberInputMax"
          :min="numberInputMin"
          controlVariant="split"
          :aria-label="minAriaLabel"
          @update:modelValue="onMinChanged"
          @change="handleUpdate"
          @keydown.enter="handleUpdate"
          variant="outlined"
          bg-color="surface-bright"
          color="primary"
          class="number-inputs"
          :hide-details="true"
        ></v-number-input>
        <v-col class="ma-2 pa-0" cols="auto" :aria-hidden="true">
          <span>&mdash;</span>
        </v-col>
        <FilterFormattedStepper
          v-if="props.valueLabelMappings"
          :model-value="range[1]"
          :min="minRangeValue"
          :max="maxRangeValue"
          :value-label-mappings="props.valueLabelMappings"
          :ariaLabel="maxAriaLabel"
          class="formatted-inputs"
          @update:modelValue="onMaxChanged"
          @change="handleUpdate"
        />
        <v-number-input
          v-else
          ref="maxInputRef"
          :model-value="numberInputMaxValue"
          density="compact"
          :max="numberInputMax"
          :min="numberInputMin"
          control-variant="split"
          :aria-label="maxAriaLabel"
          @update:modelValue="onMaxChanged"
          @change="handleUpdate"
          @keydown.enter="handleUpdate"
          variant="outlined"
          bg-color="surface-bright"
          color="primary"
          class="number-inputs"
          :hide-details="true"
        ></v-number-input>
      </div>
    </div>
  </template>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

.range-container {
  padding: 35px 10px 15px 10px;
}
:deep(.v-slider-thumb__label) {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  &:before {
    color: rgb(var(--v-theme-primary));
  }
}

.formatted-inputs {
  width: 100%;
  max-height: 40px;
}

.number-inputs {
  max-height: 40px;
  :deep(.v-field__input) {
    text-align: center !important;
    font-size: 0.875rem !important;
  }

  :deep(.v-btn) {
    color: rgb(var(--v-theme-on-surface)) !important;
    background-color: transparent !important;
  }

  :deep(.v-btn[disabled]) {
    opacity: 0.26 !important; /* Apply opacity when button is disabled */
  }

  @media #{map.get(v.$display-breakpoints, 'sm-and-up')} {
    :deep(.v-btn--icon) {
      width: calc(var(--v-btn-height)) !important;
    }
  }

  @media #{map.get(v.$display-breakpoints, 'sm-and-down')} {
    :deep(.v-btn--icon) {
      width: calc(var(--v-btn-height) + 12px) !important;
    }
  }

  @media (max-width: 400px) {
    :deep(.v-btn--icon) {
      width: calc(var(--v-btn-height) - 12px) !important;
    }
  }
}
</style>
